import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  IIcLookupSettingsDto,
  IcLookupSettingsDto,
  IcLookupTypeEnumDto,
  ItemCacheClient,
} from '@data-access/bulk-operations-api';
import { WebSocketClient, WsServerPayloadEnum } from '@data-access/bulk-operations-ws';
import { EMPTY, delayWhen, expand, filter, finalize, iif, of, reduce, take } from 'rxjs';

import { lookupReducer } from '../helpers/lookup-reducer';

@Injectable({ providedIn: 'root' })
export class IcSettingsService {
  protected readonly client = inject(ItemCacheClient);
  protected readonly wsClient = inject(WebSocketClient);

  public readonly needsOnboardingSignal = signal<boolean | undefined>(undefined);
  public readonly segmentErrorSignal = signal<string | undefined>(undefined);

  public readonly isInitializedSignal = signal(false);
  protected readonly isInitialized$ = toObservable(this.isInitializedSignal);
  public readonly isLoadingSignal = signal(false);

  private isManualSync = false;

  private readonly _valueSignal = signal<IIcLookupSettingsDto>(new IcLookupSettingsDto());
  public readonly valueSignal = this._valueSignal.asReadonly();

  constructor() {
    this.subscribeToWsSyncComplete();
    this.subscribeToWsUpsert();
  }

  public fetchValues() {
    this.isLoadingSignal.set(true);
    this.client
      .getIcLookup(IcLookupTypeEnumDto.Settings)
      .pipe(
        take(1),
        expand((response) =>
          response.lookup.nextToken
            ? this.client.getIcLookup(IcLookupTypeEnumDto.Settings, response.lookup.nextToken)
            : EMPTY,
        ),
        reduce(lookupReducer),
        finalize(() => this.isLoadingSignal.set(false)),
      )
      .subscribe((data) => {
        this.setCache(data.lookup.settings);
      });
  }

  public setCache(lookupValue: IIcLookupSettingsDto, isInitialization = false) {
    this._valueSignal.set(lookupValue);
    if (!isInitialization) {
      this.isInitializedSignal.set(true);
    }
  }

  public startSync() {
    this.client
      .getIcStartSync(IcLookupTypeEnumDto.Settings)
      .pipe(take(1))
      .subscribe(() => {
        this.isManualSync = true;
      });
  }

  protected subscribeToWsUpsert() {
    this.wsClient
      .getMessagesByType(WsServerPayloadEnum.IcUpserted)
      .pipe(
        filter((msg) => msg.type === IcLookupTypeEnumDto.Settings),
        // Hold processing to avoid overwrites
        delayWhen(() =>
          iif(
            () => this.isInitializedSignal(),
            of(true),
            this.isInitialized$.pipe(filter((isInitialized) => isInitialized)),
          ),
        ),
      )
      .subscribe((msg) => {
        this.setCache(msg.item);
      });
  }

  private subscribeToWsSyncComplete() {
    this.wsClient
      .getMessagesByType(WsServerPayloadEnum.IcSyncCompleted)
      .pipe(filter((msg) => msg.type === IcLookupTypeEnumDto.Settings))
      .subscribe((msg) => {
        if (msg.isInitialization || this.isManualSync) {
          this.fetchValues();
        }
      });
  }
}
